/**
 * Networks screen — staff-internal view of the three resource networks
 * NUREV plugs into:
 *
 *   - Mysterium  (VPN exit-node bandwidth relay)
 *   - IPRoyal    (Residential proxy bandwidth)
 *   - Sentinel   (Distributed storage)
 *
 * Each tab surfaces:
 *   - Active node count + connection status
 *   - Throughput / capacity / earnings KPIs (kind-aware: bandwidth vs storage)
 *   - Top earner ranking table
 *
 * Tab is tracked in URL search params (?tab=mysterium) for shareable links,
 * matching the legacy dashboard behaviour (react-router v5 useHistory /
 * useLocation replaced by react-router v7 useNavigate / useSearchParams).
 *
 * Data is fetched via useNetworkTab() which polls every 30 s and treats
 * 404 as a NOT_CONFIGURED empty state rather than an error.
 */
import { useSearchParams } from 'react-router-dom';
import { Globe, Wifi, HardDrive, ExternalLink, RefreshCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useNetworkTab } from '@/hooks/useNetworks';
import type { NetworkData, NetworkTab, TopNode } from '@/api/networks';
import { Card, SectionLabel, Skeleton, StatCard } from '@/components/ui/atoms';
import { formatUsd, fmtNum } from '@/lib/format';

// ─── Network definitions ──────────────────────────────────────────────────

interface NetworkDef {
  id: NetworkTab;
  label: string;
  Icon: LucideIcon;
  purpose: string;
  url: string;
  kind: 'bandwidth' | 'storage';
}

const NETWORKS: NetworkDef[] = [
  {
    id: 'mysterium',
    label: 'Mysterium',
    Icon: Globe,
    purpose: 'VPN exit-node bandwidth relay',
    url: 'https://mysterium.network',
    kind: 'bandwidth'
  },
  {
    id: 'iproyal',
    label: 'IPRoyal',
    Icon: Wifi,
    purpose: 'Residential proxy bandwidth',
    url: 'https://iproyal.com',
    kind: 'bandwidth'
  },
  {
    id: 'sentinel',
    label: 'Sentinel',
    Icon: HardDrive,
    purpose: 'Distributed storage',
    url: 'https://sentinel.co',
    kind: 'storage'
  }
];

// ─── Formatters ───────────────────────────────────────────────────────────

function fmtBps(b: number | undefined | null): string {
  if (!b) return '0 bps';
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} Gbps`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(2)} Mbps`;
  if (b >= 1e3) return `${(b / 1e3).toFixed(2)} Kbps`;
  return `${Math.round(b)} bps`;
}

function fmtBytes(b: number | undefined | null): string {
  if (!b) return '0 B';
  if (b >= 1e12) return `${(b / 1e12).toFixed(2)} TB`;
  if (b >= 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b >= 1e6) return `${(b / 1e6).toFixed(2)} MB`;
  return `${Math.round(b)} B`;
}

// ─── Not-configured empty state ───────────────────────────────────────────

function NotConfigured({ network }: { network: NetworkDef }) {
  const { Icon } = network;
  return (
    <Card>
      <div className="flex flex-col items-center py-10 text-center">
        <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-nurev-border bg-white/[0.04]">
          <Icon className="h-6 w-6 text-nurev-muted" />
        </div>
        <p className="mb-1 text-sm font-bold text-nurev-text">
          {network.label} not configured
        </p>
        <p className="max-w-md text-xs text-nurev-muted">
          Set the {network.id.toUpperCase()} env vars in your nurev-relay K8s
          secret to enable live monitoring. Backend endpoint{' '}
          <code className="rounded bg-white/[0.04] px-1 py-0.5 font-mono text-[10px] text-amber-300">
            /admin-services/networks/{network.id}
          </code>{' '}
          begins reporting once a node check-in is received.
        </p>
      </div>
    </Card>
  );
}

// ─── KPI grid — bandwidth networks ───────────────────────────────────────

function BandwidthKpis({
  data,
  loading
}: {
  data: NetworkData;
  loading: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Active Nodes"
        value={fmtNum(data.activeNodes)}
        sub="running NUREV binary"
        loading={loading}
      />
      <StatCard
        label="Throughput"
        value={fmtBps(data.throughputBps)}
        sub="aggregate live"
        loading={loading}
      />
      <StatCard
        label="Data Routed (24h)"
        value={fmtBytes(data.bytesRouted24h)}
        loading={loading}
      />
      <StatCard
        label="Earnings (24h)"
        value={formatUsd(data.earnings24hUsd)}
        color="text-emerald-300"
        loading={loading}
      />
    </div>
  );
}

// ─── KPI grid — storage networks ──────────────────────────────────────────

function StorageKpis({
  data,
  loading
}: {
  data: NetworkData;
  loading: boolean;
}) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard
        label="Active Nodes"
        value={fmtNum(data.activeNodes)}
        sub="running NUREV binary"
        loading={loading}
      />
      <StatCard
        label="Allocated Space"
        value={fmtBytes(data.allocatedBytes)}
        loading={loading}
      />
      <StatCard
        label="Audits Passed (24h)"
        value={fmtNum(data.auditsPassed24h)}
        sub={
          (data.auditsFailed24h ?? 0) > 0
            ? `${fmtNum(data.auditsFailed24h)} failed`
            : undefined
        }
        color="text-emerald-300"
        loading={loading}
      />
      <StatCard
        label="Earnings (24h)"
        value={formatUsd(data.earnings24hUsd)}
        color="text-emerald-300"
        loading={loading}
      />
    </div>
  );
}

// ─── Top earners table ────────────────────────────────────────────────────

function TopEarnersTable({ nodes }: { nodes: TopNode[] }) {
  if (nodes.length === 0) return null;
  return (
    <Card>
      <SectionLabel>Top Earning Nodes</SectionLabel>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-nurev-muted">
              <th className="pb-2.5 font-medium">Node</th>
              <th className="pb-2.5 font-medium">Country</th>
              <th className="pb-2.5 text-right font-medium">Uptime</th>
              <th className="pb-2.5 text-right font-medium">Earnings</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-nurev-border">
            {nodes.map(n => (
              <tr key={n.id}>
                <td className="py-2.5 font-mono text-[11px] text-nurev-text">
                  {n.id}
                </td>
                <td className="py-2.5 text-nurev-muted">{n.country || '—'}</td>
                <td className="py-2.5 text-right text-nurev-text">
                  {n.uptime != null ? `${n.uptime.toFixed(1)}%` : '—'}
                </td>
                <td className="py-2.5 text-right font-bold text-emerald-300">
                  {formatUsd(n.earnings)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ─── Network panel (when configured) ─────────────────────────────────────

function NetworkPanel({
  network,
  data,
  loading
}: {
  network: NetworkDef;
  data: NetworkData;
  loading: boolean;
}) {
  const statusLabel =
    data.status === 'down' ? 'Down' : 'Connected';
  const statusCls =
    data.status === 'down'
      ? 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
      : 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30';

  return (
    <div className="space-y-4">
      {/* Network header */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-nurev-border bg-nurev-surface p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-nurev-border bg-white/[0.05]">
            <network.Icon className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <p className="text-sm font-bold text-nurev-text">{network.label}</p>
            <p className="text-[11px] text-nurev-muted">{network.purpose}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-block rounded px-2 py-0.5 text-[10px] font-medium ${statusCls}`}
          >
            {statusLabel}
          </span>
          <a
            href={network.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-[11px] font-bold text-nurev-text no-underline transition hover:bg-white/[0.07]"
          >
            Vendor dashboard
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* KPIs */}
      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : network.kind === 'bandwidth' ? (
        <BandwidthKpis data={data} loading={loading} />
      ) : (
        <StorageKpis data={data} loading={loading} />
      )}

      {/* Top earners */}
      {!loading && data.topNodes?.length > 0 && (
        <TopEarnersTable nodes={data.topNodes} />
      )}
    </div>
  );
}

// ─── Tab content wrapper ──────────────────────────────────────────────────

function NetworkTabContent({ network }: { network: NetworkDef }) {
  const { data, isLoading, refetch, isFetching, dataUpdatedAt } =
    useNetworkTab(network.id);

  const updatedAt = dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  const configured = data?.configured ?? false;

  return (
    <div className="space-y-4">
      {/* Refresh bar */}
      <div className="flex items-center justify-end gap-2">
        {updatedAt && (
          <span className="text-[10px] text-nurev-muted">
            updated {updatedAt.toLocaleTimeString()}
          </span>
        )}
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-1.5 rounded-lg border border-nurev-border bg-white/[0.04] px-3 py-1.5 text-xs font-bold text-nurev-text transition hover:bg-white/[0.07] disabled:opacity-50"
        >
          <RefreshCw
            className={`h-3 w-3 ${isFetching ? 'animate-spin' : ''}`}
          />
          Refresh
        </button>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : !configured || !data ? (
        <NotConfigured network={network} />
      ) : (
        <NetworkPanel network={network} data={data} loading={isFetching} />
      )}
    </div>
  );
}

// ─── Main screen ──────────────────────────────────────────────────────────

export function NetworksScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab: NetworkTab =
    NETWORKS.find(n => n.id === rawTab)?.id ?? 'mysterium';

  const handleTabChange = (id: NetworkTab) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const network = NETWORKS.find(n => n.id === activeTab)!;

  return (
    <div className="mx-auto max-w-7xl space-y-5">
      {/* Page header */}
      <header>
        <h1 className="text-xl font-bold text-nurev-text lg:text-2xl">
          Resource Networks
        </h1>
        <p className="mt-1 text-xs text-nurev-muted">
          Bandwidth + storage networks NUREV plugs into. Polls every 30 seconds.
        </p>
      </header>

      {/* Tab strip */}
      <div className="flex flex-wrap gap-2">
        {NETWORKS.map(n => {
          const isActive = n.id === activeTab;
          return (
            <button
              key={n.id}
              onClick={() => handleTabChange(n.id)}
              className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-xs font-medium transition ${
                isActive
                  ? 'bg-amber-500 text-nurev-bg'
                  : 'text-nurev-muted hover:bg-white/[0.04] hover:text-nurev-text'
              }`}
            >
              <n.Icon className="h-3.5 w-3.5" />
              {n.label}
            </button>
          );
        })}
      </div>

      {/* Active tab content — mounts separately per tab so each has its own
          query lifecycle (polling, last-updated timestamp, refetch button). */}
      {activeTab === 'mysterium' && <NetworkTabContent network={network} />}
      {activeTab === 'iproyal' && <NetworkTabContent network={network} />}
      {activeTab === 'sentinel' && <NetworkTabContent network={network} />}
    </div>
  );
}
